import createDebug from '../utils/debug';
import type { Manifest } from '../shared/types';
import type { StorageLike } from '../utils/namespaced-storage';

const debug = createDebug('manifest:cache');
const getCacheKey = (appName: string): string => `app-manifest:${appName}`;

export function getCachedManifest(
  appName: string,
  storage: StorageLike,
  expiresIn: number
): object | null {
  const cachedManifestData = storage.getItem(getCacheKey(appName));

  // exit if no cached manifest available
  if (!cachedManifestData) return null;

  // parse cached manifest data
  let createdAt: unknown;
  let manifest: unknown;
  try {
    ({ createdAt, manifest } = JSON.parse(cachedManifestData));
  } catch (err) {
    console.warn('Failed to parse cached manifest data', err);
    return null; // exit on error
  }

  // Shallow shape check only — NOT real Manifest validation. This just
  // confirms we have a timestamp and a non-null object; whether that object
  // actually satisfies Manifest is decided by validateAppManifest, which
  // every caller here runs on the result before trusting it (see
  // manifest-loader.ts).
  if (
    typeof createdAt !== 'number' ||
    typeof manifest !== 'object' ||
    manifest === null
  ) {
    return null;
  }

  // do not return expired manifest
  if (Date.now() - createdAt > expiresIn) {
    return null;
  }

  // return cached manifest
  debug('use cached manifest', appName, manifest);
  return manifest;
}

export function cacheManifest(
  appName: string,
  manifest: Manifest,
  storage: StorageLike
): void {
  const data = JSON.stringify({ createdAt: Date.now(), manifest });
  storage.setItem(getCacheKey(appName), data);
  debug('cache manifest', appName, manifest);
}
